
import React from "react";

const QUESTION_TYPE_LABELS = {
  singleChoice: "Single Choice",
  multipleChoice: "Multiple Choice",
  statementBased: "Statement Based",
  synonym: "Synonym",
  antonym: "Antonym",
  vocabulary: "Vocabulary",
  fillInTheBlank: "Fill in the Blank",
  errorDetection: "Error Detection",
  sentenceImprovement: "Sentence Improvement",
  phraseReplacement: "Phrase Replacement",
  clozeTest: "Cloze Test",
  paraJumble: "Para Jumble",
  matchFollowing: "Match the Following",
};

const DIFFICULTY_STYLES = {
  easy: "bg-green-50 text-green-700 ring-green-600/20",
  medium:
    "bg-yellow-50 text-yellow-700 ring-yellow-600/20",
  hard: "bg-red-50 text-red-700 ring-red-600/20",
};

const QUESTION_TYPE_STYLES = {
  singleChoice:
    "bg-blue-50 text-blue-700 ring-blue-600/20",
  multipleChoice:
    "bg-purple-50 text-purple-700 ring-purple-600/20",
  statementBased:
    "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  synonym:
    "bg-cyan-50 text-cyan-700 ring-cyan-600/20",
  antonym:
    "bg-orange-50 text-orange-700 ring-orange-600/20",
  vocabulary:
    "bg-teal-50 text-teal-700 ring-teal-600/20",
  fillInTheBlank:
    "bg-pink-50 text-pink-700 ring-pink-600/20",
  errorDetection:
    "bg-rose-50 text-rose-700 ring-rose-600/20",
  sentenceImprovement:
    "bg-violet-50 text-violet-700 ring-violet-600/20",
  phraseReplacement:
    "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20",
  clozeTest:
    "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  paraJumble:
    "bg-amber-50 text-amber-700 ring-amber-600/20",
  matchFollowing:
    "bg-slate-100 text-slate-700 ring-slate-600/20",
};

const getId = (value) =>
  value?._id || value || null;

const getName = (value, fallback = "—") => {
  if (!value) return fallback;

  if (typeof value === "string") {
    return value;
  }

  return (
    value.name ||
    value.title ||
    value.groupName ||
    fallback
  );
};

const truncateText = (text, length = 100) => {
  if (!text) return "—";

  if (text.length <= length) {
    return text;
  }

  return `${text.substring(0, length)}...`;
};

const formatQuestionType = (type) =>
  QUESTION_TYPE_LABELS[type] ||
  type
    ?.replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) =>
      char.toUpperCase(),
    ) ||
  "—";

const EnglishQuestionTable = ({
  questions = [],
  loading = false,
  error = null,

  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  onRecalculate,

  deletingId = null,
  togglingId = null,
  recalculatingId = null,

  showSubject = true,
  showChapter = true,
  showGroup = true,
  showActions = true,

  emptyMessage = "No English questions found.",
}) => {
  if (loading) {
    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="hidden md:block">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex gap-4">
              {[1, 2, 3, 4, 5].map(
                (item) => (
                  <div
                    key={item}
                    className="h-4 flex-1 animate-pulse rounded bg-gray-200"
                  />
                ),
              )}
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {Array.from({ length: 6 }).map(
              (_, index) => (
                <div
                  key={index}
                  className="flex gap-4 px-6 py-5"
                >
                  <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-100" />

                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
                  </div>

                  <div className="h-6 w-20 animate-pulse rounded-full bg-gray-100" />

                  <div className="h-8 w-24 animate-pulse rounded bg-gray-100" />
                </div>
              ),
            )}
          </div>
        </div>

        <div className="space-y-4 p-4 md:hidden">
          {Array.from({ length: 4 }).map(
            (_, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-200 p-4"
              >
                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                <div className="mt-3 h-3 w-1/2 animate-pulse rounded bg-gray-100" />
                <div className="mt-4 h-8 w-full animate-pulse rounded bg-gray-100" />
              </div>
            ),
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
          !
        </div>

        <h3 className="mt-3 text-sm font-semibold text-red-800">
          Unable to load questions
        </h3>

        <p className="mt-1 text-sm text-red-600">
          {typeof error === "string"
            ? error
            : error?.data?.message ||
              error?.message ||
              "Something went wrong while loading the questions."}
        </p>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
          ?
        </div>

        <h3 className="mt-4 text-sm font-semibold text-gray-900">
          No questions
        </h3>

        <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const renderDifficulty = (difficulty) => {
    if (!difficulty) {
      return (
        <span className="text-gray-400">
          —
        </span>
      );
    }

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset ${
          DIFFICULTY_STYLES[difficulty] ||
          "bg-gray-50 text-gray-600 ring-gray-500/20"
        }`}
      >
        {difficulty}
      </span>
    );
  };

  const renderQuestionType = (
    questionType,
  ) => (
    <span
      className={`inline-flex max-w-[180px] items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        QUESTION_TYPE_STYLES[
          questionType
        ] ||
        "bg-gray-50 text-gray-600 ring-gray-500/20"
      }`}
    >
      <span className="truncate">
        {formatQuestionType(
          questionType,
        )}
      </span>
    </span>
  );

  const renderStatus = (question) => {
    const active =
      question.isActive !== false;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
          active
            ? "bg-green-50 text-green-700 ring-green-600/20"
            : "bg-gray-100 text-gray-600 ring-gray-500/20"
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            active
              ? "bg-green-500"
              : "bg-gray-400"
          }`}
        />

        {active
          ? "Active"
          : "Inactive"}
      </span>
    );
  };

  const renderActions = (question) => {
    const id = getId(question);

    return (
      <div className="flex items-center justify-end gap-1">
        {onView && (
          <button
            type="button"
            onClick={() =>
              onView(question)
            }
            className="rounded-lg px-2.5 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
            title="View question"
          >
            View
          </button>
        )}

        {onEdit && (
          <button
            type="button"
            onClick={() =>
              onEdit(question)
            }
            className="rounded-lg px-2.5 py-2 text-xs font-medium text-indigo-600 transition hover:bg-indigo-50"
            title="Edit question"
          >
            Edit
          </button>
        )}

        {onToggleStatus && (
          <button
            type="button"
            onClick={() =>
              onToggleStatus(question)
            }
            disabled={
              togglingId &&
              String(togglingId) ===
                String(id)
            }
            className="rounded-lg px-2.5 py-2 text-xs font-medium text-amber-600 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              question.isActive === false
                ? "Activate question"
                : "Deactivate question"
            }
          >
            {togglingId &&
            String(togglingId) ===
              String(id)
              ? "..."
              : question.isActive === false
                ? "Activate"
                : "Deactivate"}
          </button>
        )}

        {onRecalculate && (
          <button
            type="button"
            onClick={() =>
              onRecalculate(question)
            }
            disabled={
              recalculatingId &&
              String(recalculatingId) ===
                String(id)
            }
            className="rounded-lg px-2.5 py-2 text-xs font-medium text-purple-600 transition hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
            title="Recalculate question"
          >
            {recalculatingId &&
            String(recalculatingId) ===
              String(id)
              ? "..."
              : "Recalculate"}
          </button>
        )}

        {onDelete && (
          <button
            type="button"
            onClick={() =>
              onDelete(question)
            }
            disabled={
              deletingId &&
              String(deletingId) ===
                String(id)
            }
            className="rounded-lg px-2.5 py-2 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            title="Delete question"
          >
            {deletingId &&
            String(deletingId) ===
              String(id)
              ? "..."
              : "Delete"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Desktop Table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="w-12 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                #
              </th>

              <th
                scope="col"
                className="min-w-[320px] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Question
              </th>

              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Type
              </th>

              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Difficulty
              </th>

              {showSubject && (
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Subject
                </th>
              )}

              {showChapter && (
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Chapter
                </th>
              )}

              {showGroup && (
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Group
                </th>
              )}

              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Marks
              </th>

              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Status
              </th>

              {showActions && (
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {questions.map(
              (question, index) => {
                const id = getId(question);

                return (
                  <tr
                    key={id || index}
                    className="transition hover:bg-gray-50/80"
                  >
                    {/* Number */}
                    <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-gray-500">
                      {question.order ??
                        index + 1}
                    </td>

                    {/* Question */}
                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() =>
                          onView?.(question)
                        }
                        className={`text-left ${
                          onView
                            ? "cursor-pointer"
                            : "cursor-default"
                        }`}
                      >
                        <div className="max-w-[420px]">
                          <p className="line-clamp-2 text-sm font-medium text-gray-900">
                            {truncateText(
                              question.questionText,
                              140,
                            )}
                          </p>

                          {question.highlightedText && (
                            <p className="mt-1 line-clamp-1 text-xs text-gray-500">
                              Highlighted:{" "}
                              {truncateText(
                                question.highlightedText,
                                70,
                              )}
                            </p>
                          )}

                          {Array.isArray(
                            question.options,
                          ) &&
                            question.options
                              .length > 0 && (
                              <p className="mt-1 text-xs text-gray-400">
                                {
                                  question
                                    .options
                                    .length
                                }{" "}
                                options
                              </p>
                            )}
                        </div>
                      </button>
                    </td>

                    {/* Type */}
                    <td className="whitespace-nowrap px-4 py-4 align-top">
                      {renderQuestionType(
                        question.questionType,
                      )}
                    </td>

                    {/* Difficulty */}
                    <td className="whitespace-nowrap px-4 py-4 align-top">
                      {renderDifficulty(
                        question.difficulty,
                      )}
                    </td>

                    {/* Subject */}
                    {showSubject && (
                      <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-gray-600">
                        {getName(
                          question.subject,
                        )}
                      </td>
                    )}

                    {/* Chapter */}
                    {showChapter && (
                      <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-gray-600">
                        {getName(
                          question.chapter,
                        )}
                      </td>
                    )}

                    {/* Group */}
                    {showGroup && (
                      <td className="whitespace-nowrap px-4 py-4 align-top">
                        {question.group ? (
                          <span className="inline-flex max-w-[150px] rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                            <span className="truncate">
                              {getName(
                                question.group,
                                "Group",
                              )}
                            </span>
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">
                            No group
                          </span>
                        )}
                      </td>
                    )}

                    {/* Marks */}
                    <td className="whitespace-nowrap px-4 py-4 align-top">
                      <div className="text-sm font-medium text-gray-900">
                        {question.marks ??
                          0}
                      </div>

                      <div className="mt-0.5 text-xs text-red-500">
                        -{" "}
                        {question.negativeMarks ??
                          0}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="whitespace-nowrap px-4 py-4 align-top">
                      {renderStatus(
                        question,
                      )}
                    </td>

                    {/* Actions */}
                    {showActions && (
                      <td className="whitespace-nowrap px-4 py-4 align-top">
                        {renderActions(
                          question,
                        )}
                      </td>
                    )}
                  </tr>
                );
              },
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="divide-y divide-gray-100 md:hidden">
        {questions.map(
          (question, index) => {
            const id = getId(question);

            return (
              <div
                key={id || index}
                className="p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold text-gray-600">
                      {question.order ??
                        index + 1}
                    </div>

                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() =>
                          onView?.(question)
                        }
                        className="text-left"
                      >
                        <p className="line-clamp-3 text-sm font-semibold text-gray-900">
                          {question.questionText ||
                            "Untitled question"}
                        </p>
                      </button>

                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {renderQuestionType(
                          question.questionType,
                        )}

                        {renderDifficulty(
                          question.difficulty,
                        )}
                      </div>
                    </div>
                  </div>

                  {renderStatus(
                    question,
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3">
                  {showSubject && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Subject
                      </p>

                      <p className="mt-1 truncate text-xs text-gray-700">
                        {getName(
                          question.subject,
                        )}
                      </p>
                    </div>
                  )}

                  {showChapter && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Chapter
                      </p>

                      <p className="mt-1 truncate text-xs text-gray-700">
                        {getName(
                          question.chapter,
                        )}
                      </p>
                    </div>
                  )}

                  {showGroup && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Group
                      </p>

                      <p className="mt-1 truncate text-xs text-gray-700">
                        {getName(
                          question.group,
                          "No group",
                        )}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Marks
                    </p>

                    <p className="mt-1 text-xs font-medium text-gray-700">
                      {question.marks ??
                        0}{" "}
                      <span className="text-red-500">
                        / -
                        {question.negativeMarks ??
                          0}
                      </span>
                    </p>
                  </div>
                </div>

                {showActions && (
                  <div className="mt-3 flex flex-wrap justify-end gap-1 border-t border-gray-100 pt-3">
                    {renderActions(
                      question,
                    )}
                  </div>
                )}
              </div>
            );
          },
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Showing{" "}
            <span className="font-medium text-gray-700">
              {questions.length}
            </span>{" "}
            question
            {questions.length !== 1
              ? "s"
              : ""}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnglishQuestionTable;

